import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, ExternalLink, ArrowLeft, Webhook, KeyRound, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CopyBox({ label, value, testid }) {
  return (
    <div className="rounded-xl bg-slate-50/70 border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-widest text-purple-700">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <code className="text-slate-900 text-sm font-mono break-all flex-1">{value}</code>
        <Button size="sm" variant="ghost" onClick={()=>{navigator.clipboard.writeText(value); toast.success("Copied");}} data-testid={testid} className="text-purple-700"><Copy size={13}/></Button>
      </div>
    </div>
  );
}

const CODE_HELPER = `// Zapier · Code by Zapier (JavaScript, Run Javascript step)
// Build the HMAC-SHA256 signature Skool webhook expects.
const crypto = require('crypto');
const secret = inputData.secret;
// IMPORTANT: use the RAW body string that will be sent — same string
// you place into the "Data" field of the POST step below.
const body = inputData.body;
const sig = crypto.createHmac('sha256', secret).update(body).digest('hex');
output = { signature: 'sha256=' + sig };`;

const SAMPLE_BODY = `{"event":"new_paid_member","email":"{{member.email}}","id":"{{event_id}}","member":{"name":"{{member.full_name}}"}}`;

const EVENT_MAP = [
  { skool: "New member joined (paid)", event: "new_paid_member", action: "→ Restore / grant access" },
  { skool: "Member cancelled", event: "membership_cancelled", action: "→ Revoke access" },
  { skool: "Member payment failed / expired", event: "membership_expired", action: "→ Revoke access" },
  { skool: "Member removed", event: "member_removed", action: "→ Revoke access" },
  { skool: "Member reactivated", event: "membership_restored", action: "→ Restore access" },
];

export default function ZapierGuide() {
  const [wh, setWh] = useState({ enabled: false, has_secret: false });
  useEffect(() => { api.get("/admin/webhook/settings").then(r => setWh(r.data)).catch(()=>{}); }, []);

  const endpoint = `${BACKEND_URL}/api/webhooks/skool`;

  return (
    <AppShell>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Setup guide</div>
          <h1 className="text-3xl font-semibold text-slate-900 mt-1">Connect Skool to Informed Accelerator via Zapier</h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">Five-minute playbook. Skool doesn't publish a native webhook API, so we use Zapier to translate community events into HMAC-signed calls to your Informed Accelerator webhook endpoint.</p>
        </div>
        <Link to="/app/admin" data-testid="zapier-back-link">
          <Button variant="outline" className="rounded-xl border-slate-200 bg-slate-50/70 text-slate-800"><ArrowLeft size={14} className="mr-1"/>Back to Admin</Button>
        </Link>
      </div>

      {/* Status */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="glass rounded-2xl"><CardContent className="p-4 flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-purple-500/15 border border-purple-400/20 grid place-items-center"><Webhook size={16} className="text-purple-700"/></div>
          <div><div className="text-xs text-slate-500">Webhook</div>
          <div className={`text-sm font-semibold ${wh.enabled?"text-emerald-300":"text-amber-300"}`} data-testid="zapier-status-enabled">{wh.enabled?"Enabled":"Disabled"}</div></div>
        </CardContent></Card>
        <Card className="glass rounded-2xl"><CardContent className="p-4 flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-purple-500/15 border border-purple-400/20 grid place-items-center"><KeyRound size={16} className="text-purple-700"/></div>
          <div><div className="text-xs text-slate-500">Secret</div>
          <div className={`text-sm font-semibold ${wh.has_secret?"text-emerald-300":"text-amber-300"}`} data-testid="zapier-status-secret">{wh.has_secret?"Configured":"Not set"}</div></div>
        </CardContent></Card>
        <Card className="glass rounded-2xl"><CardContent className="p-4 flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-purple-500/15 border border-purple-400/20 grid place-items-center"><ShieldCheck size={16} className="text-purple-700"/></div>
          <div><div className="text-xs text-slate-500">Signature</div><div className="text-sm font-semibold text-slate-800">HMAC-SHA256</div></div>
        </CardContent></Card>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Steps */}
        <div className="lg:col-span-2 space-y-4">
          {[
            {n:1, t:"Turn on the webhook in Informed Accelerator", body:(
              <div className="space-y-3">
                <p className="text-sm text-slate-700">Open Admin → <b>Skool Webhook</b>, flip <b>Enabled</b> on, paste a strong random string as the <b>secret</b>, and save. Keep the secret handy for Step 3.</p>
                <Link to="/app/admin" data-testid="zapier-go-admin"><Button size="sm" className="btn-primary rounded-xl">Open Admin → Webhook</Button></Link>
              </div>
            )},
            {n:2, t:"Create a Skool trigger in Zapier", body:(
              <div className="space-y-3">
                <p className="text-sm text-slate-700">In Zapier, choose <b>Skool</b> as the trigger app. Pick the event that matches the community change (see mapping below). Test the trigger so Zapier learns the payload shape.</p>
                <a href="https://zapier.com/apps/skool/integrations" target="_blank" rel="noreferrer" data-testid="zapier-open-skool"><Button size="sm" variant="outline" className="rounded-xl border-slate-200 bg-slate-50/70 text-slate-800">Open Skool on Zapier <ExternalLink size={13} className="ml-1"/></Button></a>
              </div>
            )},
            {n:3, t:'Add a "Code by Zapier" step to sign the payload', body:(
              <div className="space-y-3">
                <p className="text-sm text-slate-700">Add a <b>Code by Zapier (Run Javascript)</b> step between the trigger and the webhook POST. This step computes the HMAC signature.</p>
                <div className="rounded-xl bg-slate-100 border border-slate-200 p-4 overflow-auto">
                  <pre className="text-[12px] text-slate-800 whitespace-pre font-mono" data-testid="zapier-code-block">{CODE_HELPER}</pre>
                </div>
                <div className="text-xs text-slate-500"><b>Input Data</b> for the step:</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <CopyBox label="secret" value="<your webhook secret from Step 1>" testid="zapier-copy-secretname"/>
                  <CopyBox label="body" value={SAMPLE_BODY} testid="zapier-copy-body"/>
                </div>
                <p className="text-xs text-slate-400">Use Zapier field mapping to fill <code>{"{{member.email}}"}</code>, <code>{"{{event_id}}"}</code>, and <code>{"{{member.full_name}}"}</code> from your Skool trigger.</p>
              </div>
            )},
            {n:4, t:"POST to the Informed Accelerator webhook", body:(
              <div className="space-y-3">
                <p className="text-sm text-slate-700">Add a <b>Webhooks by Zapier · POST</b> action.</p>
                <div className="grid grid-cols-1 gap-2">
                  <CopyBox label="URL" value={endpoint} testid="zapier-copy-url"/>
                  <CopyBox label="Payload Type" value="raw (JSON string)" testid="zapier-copy-payload-type"/>
                  <CopyBox label="Data (raw body)" value={"{{body}}    ← the same body you signed in Step 3"} testid="zapier-copy-data"/>
                </div>
                <div className="text-xs text-slate-500">Headers:</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <CopyBox label="Content-Type" value="application/json" testid="zapier-copy-content-type"/>
                  <CopyBox label="X-Skool-Signature" value="{{signature}}   ← from Step 3" testid="zapier-copy-sig"/>
                  <CopyBox label="X-Event-Id" value="{{event_id}}   ← unique per Skool event" testid="zapier-copy-eventid"/>
                </div>
              </div>
            )},
            {n:5, t:"Repeat for each Skool event → Informed Accelerator event", body:(
              <div className="space-y-3">
                <p className="text-sm text-slate-700">Duplicate the Zap for each Skool event you want to react to. Set the <code>event</code> field in the body to match:</p>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase text-slate-500 bg-slate-50/70">
                      <tr><th className="p-3">Skool trigger</th><th className="p-3">Set body.event to</th><th className="p-3">Result in Informed Accelerator</th></tr>
                    </thead>
                    <tbody>
                      {EVENT_MAP.map((r)=>(
                        <tr key={r.event} className="border-t border-slate-200">
                          <td className="p-3 text-slate-800">{r.skool}</td>
                          <td className="p-3 font-mono text-purple-700">{r.event}</td>
                          <td className="p-3 text-slate-700">{r.action}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )},
            {n:6, t:"Test end-to-end", body:(
              <div className="space-y-3">
                <p className="text-sm text-slate-700">In Zapier, run <b>Test action</b>. Then open <Link className="text-purple-700 underline" to="/app/admin" data-testid="zapier-audit-link">Admin → Skool Webhook</Link> — you should see the event appear with a status of <code className="text-emerald-300">user_restored</code>, <code className="text-amber-300">user_revoked</code>, or <code className="text-slate-500">user_not_found</code>.</p>
                <p className="text-xs text-slate-400">Duplicate <b>X-Event-Id</b> values are ignored — safe to retry.</p>
              </div>
            )},
          ].map((s)=>(
            <Card key={s.n} className="glass rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-9 w-9 shrink-0 rounded-xl bg-purple-500/15 border border-purple-400/30 grid place-items-center text-purple-700 font-semibold">{s.n}</div>
                  <div className="flex-1">
                    <div className="text-slate-900 font-semibold">{s.t}</div>
                    <div className="mt-2">{s.body}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card className="glass rounded-2xl sticky top-24">
            <CardContent className="p-6 space-y-4">
              <div>
                <div className="eyebrow">Ready-to-copy</div>
                <div className="text-slate-900 font-semibold mt-1">Your endpoint</div>
              </div>
              <CopyBox label="POST URL" value={endpoint} testid="zapier-copy-endpoint"/>
              <div>
                <div className="text-xs text-slate-500 mb-1">Sample raw body</div>
                <div className="rounded-xl bg-slate-100 border border-slate-200 p-3 overflow-auto"><pre className="text-[11px] text-slate-800 font-mono whitespace-pre-wrap">{SAMPLE_BODY}</pre></div>
              </div>
              <div className="rounded-xl bg-purple-500/10 border border-purple-400/25 p-3 text-xs text-slate-800 leading-relaxed">
                <b className="text-slate-900">Security tips</b>
                <ul className="list-disc list-inside mt-1 space-y-1 text-slate-700">
                  <li>Use a random 32+ char secret. Rotate it if leaked.</li>
                  <li>Sign the raw body — do not let Zapier reformat JSON between Step 3 and Step 4.</li>
                  <li>Set a unique <code>X-Event-Id</code> per event to prevent double-processing on retries.</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
